import { supabase } from '@/lib/supabaseClient';

/**
 * Schedule table schema (existing in Supabase):
 *   id (uuid), date (date), job_id (text), crew (text[]), note (text), created_at
 *
 * Note: `job_id` here is a free-form string (NOT a strict FK to jobs.id) so
 * we can also support legacy or quick-text assignments.
 */
const FIELDS = 'id,date,job_id,crew,note,created_at';

export const isoDate = (date) => {
  const d = date instanceof Date ? date : new Date(date);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

export const sameIsoDate = (a, b) => isoDate(a) === isoDate(b);

export const startOfMonth = (date) => {
  const d = new Date(date);
  d.setHours(0, 0, 0, 0);
  d.setDate(1);
  return d;
};

export const endOfMonth = (date) => {
  const d = new Date(date);
  d.setHours(23, 59, 59, 999);
  d.setMonth(d.getMonth() + 1);
  d.setDate(0);
  return d;
};

export const startOfWeek = (date, { weekStartsOn = 1 } = {}) => {
  const d = new Date(date);
  d.setHours(0, 0, 0, 0);
  const day = (d.getDay() - weekStartsOn + 7) % 7;
  d.setDate(d.getDate() - day);
  return d;
};

export const endOfWeek = (date, opts) => {
  const s = startOfWeek(date, opts);
  const e = new Date(s);
  e.setDate(e.getDate() + 6);
  e.setHours(23, 59, 59, 999);
  return e;
};

export const addDays = (date, n) => {
  const d = new Date(date);
  d.setDate(d.getDate() + n);
  return d;
};

/**
 * Build a 6-row, 7-col grid of Date objects covering the entire month view.
 * Weeks start on Monday.
 */
export const buildMonthGrid = (cursor) => {
  const start = startOfMonth(cursor);
  const gridStart = startOfWeek(start);
  const days = [];
  for (let i = 0; i < 42; i++) {
    days.push(addDays(gridStart, i));
  }
  return days;
};

export const buildWeekGrid = (cursor) => {
  const start = startOfWeek(cursor);
  return Array.from({ length: 7 }, (_, i) => addDays(start, i));
};

export const formatMonthLabel = (date) =>
  new Intl.DateTimeFormat('en-US', { month: 'long', year: 'numeric' }).format(date);

export const formatDayShort = (date) =>
  new Intl.DateTimeFormat('en-US', { weekday: 'short', month: 'short', day: 'numeric' }).format(
    date
  );

/* ----------------------------------------------------------------------------
 * CRUD
 * ------------------------------------------------------------------------- */
export const fetchScheduleRange = async ({ from, to } = {}) => {
  let q = supabase.from('schedule').select(FIELDS).order('date', { ascending: true });
  if (from) q = q.gte('date', isoDate(from));
  if (to) q = q.lte('date', isoDate(to));
  const { data, error } = await q;
  if (error) throw error;
  return data || [];
};

export const groupByDate = (entries = []) => {
  const map = new Map();
  for (const e of entries) {
    const key = e?.date;
    if (!key) continue;
    if (!map.has(key)) map.set(key, []);
    map.get(key).push(e);
  }
  return map;
};

export const createAssignment = async ({ date, jobId, jobName, crew, note }) => {
  const safe = {
    date: isoDate(date),
    job_id: String(jobId || jobName || ''),
    crew: Array.isArray(crew) ? crew : [],
    note: note ? String(note) : null,
  };
  const { data, error } = await supabase
    .from('schedule')
    .insert([safe])
    .select(FIELDS)
    .single();
  if (error) throw error;
  return data;
};

export const updateAssignment = async (id, updates) => {
  const safe = {};
  if ('date' in updates) safe.date = isoDate(updates.date);
  if ('jobId' in updates || 'jobName' in updates) {
    safe.job_id = String(updates.jobId || updates.jobName || '');
  } else if ('job_id' in updates) {
    safe.job_id = String(updates.job_id || '');
  }
  if ('crew' in updates) safe.crew = Array.isArray(updates.crew) ? updates.crew : [];
  if ('note' in updates) safe.note = updates.note ? String(updates.note) : null;
  const { data, error } = await supabase
    .from('schedule')
    .update(safe)
    .eq('id', id)
    .select(FIELDS)
    .single();
  if (error) throw error;
  return data;
};

export const deleteAssignment = async (id) => {
  const { error } = await supabase.from('schedule').delete().eq('id', id);
  if (error) throw error;
  return true;
};

/**
 * Resolve an assignment's display info (job name/color) by matching the stored
 * `job_id` (which can be either the jobs.id UUID OR a free-form name) against
 * the in-memory jobs list.
 */
export const resolveAssignmentJob = (assignment, jobs = []) => {
  if (!assignment) return { jobName: '', jobKey: '', linked: null };
  const stored = String(assignment.job_id || '');
  const linked = jobs.find((j) => j.id === stored) || null;
  if (linked) return { jobName: linked.name, jobKey: linked.id, linked };
  // Fallback: try matching by name (case-insensitive)
  const byName = jobs.find(
    (j) => (j?.name || '').trim().toLowerCase() === stored.trim().toLowerCase()
  );
  if (byName) return { jobName: byName.name, jobKey: byName.id, linked: byName };
  return { jobName: stored, jobKey: stored, linked: null };
};
